import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TaskDetailModal from '../components/TaskDetailModal';
import { apiService, type ApiVersion } from '../services/apiService';
import { authService } from '../services/authService';
import { Task } from '../types/Task';

interface TaskDetailPageProps {
  initialTask?: Task | null;
}

export const TaskDetailPage: React.FC<TaskDetailPageProps> = ({ initialTask = null }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const taskId = Number(id ?? NaN);
  const [apiVersion, setApiVersion] = useState<ApiVersion>('v1');
  const [selectedTask, setSelectedTask] = useState<Task | null>(initialTask);
  const [loading, setLoading] = useState(!initialTask);
  const [error, setError] = useState<string | null>(null);

  const loadTaskDetails = useCallback(async (version: ApiVersion) => {
    if (!Number.isFinite(taskId)) {
      setError('Task details are unavailable.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      apiService.setApiVersion(version);
      const taskDetails = await apiService.getTaskById(taskId);
      setSelectedTask(taskDetails);
    } catch (err) {
      setError('Failed to load task details. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (!Number.isFinite(taskId)) {
      navigate('/', { replace: true });
      return;
    }

    const detectedVersion: ApiVersion = authService.getUser() ? 'v2' : 'v1';
    setApiVersion(detectedVersion);
    apiService.setApiVersion(detectedVersion);

    if (initialTask && detectedVersion === 'v1') {
      setSelectedTask(initialTask);
      setLoading(false);
      setError(null);
      return;
    }

    void loadTaskDetails(detectedVersion);
  }, [initialTask, loadTaskDetails, navigate, taskId]);

  const refreshSelectedTask = useCallback(async () => {
    await loadTaskDetails(apiVersion);
  }, [apiVersion, loadTaskDetails]);

  const createComment = async (text: string) => {
    if (!selectedTask?.id) {
      throw new Error('Task details are unavailable.');
    }

    await apiService.addComment(selectedTask.id, text);
    await refreshSelectedTask();
  };

  const updateComment = async (commentId: number, text: string) => {
    if (!selectedTask?.id) {
      throw new Error('Task details are unavailable.');
    }

    await apiService.updateComment(selectedTask.id, commentId, text);
    await refreshSelectedTask();
  };

  const deleteComment = async (commentId: number) => {
    if (!selectedTask?.id) {
      throw new Error('Task details are unavailable.');
    }

    await apiService.deleteComment(selectedTask.id, commentId);
    await refreshSelectedTask();
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
      return;
    }

    navigate('/', { replace: true });
  };

  return (
    <TaskDetailModal
      isOpen
      task={selectedTask}
      loading={loading}
      error={error}
      onAddComment={createComment}
      onUpdateComment={updateComment}
      onDeleteComment={deleteComment}
      onClose={handleClose}
      presentation="standalone"
    />
  );
};

export default TaskDetailPage;